import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { apiHandler } from "../../api";
import { endpoint } from "../../api/endpoint";
import { invalidateProductsCache } from "../ProductSelector/useProductSelector";
import { incrementQueueStatus, moveQueueStatus } from "../../features/Queue/QueueSlice";
import { decrementGenerationsLeft } from "../../features/Generation/GenerationSlice";
import { triggerGenerationCountRefresh } from "../Generation/useGenerationCount";

export const useImageProcess = (
  selectedModel,
  selectedBackgroundImage,
  selectedProduct,
  modelType = "mannequin",
  gender = "women",
  selectionMode = "user",
  generationOptions = {},
  generatedImage,
  setGeneratedImage,
  showMainProduct,
  setShowMainProduct,
) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const hasModelSelection = !!(
    selectedModel?.front ||
    selectedModel?.images?.[0] ||
    selectedModel?.image
  );

  const hasBackgroundSelection = !!(
    selectedBackgroundImage?.front ||
    selectedBackgroundImage?.images?.[0] ||
    selectedBackgroundImage?.image
  );

  
  const token = useSelector((state) => state.auth.token);
  const authToken = { token };
  const isValid = hasModelSelection || hasBackgroundSelection;

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const [existingQueueImages, setExistingQueueImages] = useState([]);
  const [isCheckingQueue, setIsCheckingQueue] = useState(false);

  const productNextRef = useRef(null);
  const productPrevRef = useRef(null);

  const modelNextRef = useRef(null);
  const modelPrevRef = useRef(null);

  const generatedNextRef = useRef(null);
  const generatedPrevRef = useRef(null);

  const existingNextRef = useRef(null);
  const existingPrevRef = useRef(null);

  const productSwiperRef = useRef(null);
  const modelSwiperRef = useRef(null);
  const generatedSwiperRef = useRef(null);
  const existingSwiperRef = useRef(null);

  const resolveId = (...values) => {
    const resolved = values.find(
      (value) => value !== undefined && value !== null && value !== "",
    );
    return resolved ?? null;
  };

  const productId = resolveId(
    selectedProduct?.id,
    selectedProduct?.iProductId,
    selectedProduct?.productId,
    selectedProduct?.title,
  );
  const modelId = resolveId(
    selectedModel?.id,
    selectedModel?.iModelId,
    selectedModel?.modelId,
    selectedModel?.name,
  );
  const bgId =
    selectionMode === "model" && selectedBackgroundImage
      ? resolveId(
          selectedBackgroundImage?.id,
          selectedBackgroundImage?.iBackgroundId,
          selectedBackgroundImage?.backgroundId,
          selectedBackgroundImage?.name,
        )
      : null;

  const buildGenerationConfig = () => ({
    workflow: selectionMode === "user" ? "virtual_try_on" : "generate",
    gender,
    ...generationOptions,
  });

  const getQueueModeName = () =>
    modelType === "model" ? "model_to_model" : "mannequin_to_model";

  const getQueueModeId = () =>
    getQueueModeName() === "model_to_model" ? 1 : 2;

  const buildQueuePayload = () => ({
    iModeId: getQueueModeId(),
    iGenderId: resolveId(
      selectedProduct?.iGenderId,
      selectedProduct?.genderId,
      gender === "men" ? 1 : 2,
    ),
    iModelId: resolveId(
      selectedModel?.id,
      selectedModel?.iModelId,
      selectedModel?.modelId,
    ),
    iProductId: resolveId(
      selectedProduct?.id,
      selectedProduct?.iProductId,
      selectedProduct?.productId,
    ),
    iBackgroundId:
      selectionMode === "user" || !selectedBackgroundImage
        ? null
        : resolveId(
            selectedBackgroundImage?.id,
            selectedBackgroundImage?.iBackgroundId,
            selectedBackgroundImage?.backgroundId,
          ),
    sStatus: "queued",
    sGenerationConfig: buildGenerationConfig(),
  });

  // Query existing tasks in queue for this exact Product + Model + Background combination ONCE
  useEffect(() => {
    let isCancelled = false;

    if (!token || (!productId && !modelId)) {
      setExistingQueueImages([]);
      return;
    }

    const fetchMatchingQueueItems = async () => {
      setIsCheckingQueue(true);
      try {
        const response = await apiHandler({
          url: `${endpoint.API_BASE_URL}${endpoint.QUEUE_TASKS}`,
          method: "GET",
          authToken: { token },
          noCache: true,
        });

        if (isCancelled) return;

        const items = Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response?.data)
          ? response.data
          : [];

        const matchedItems = items.filter((item) => {
          // Product matching: compare numeric IDs or titles
          const productMatch =
            (selectedProduct?.id && Number(item.iProductId) === Number(selectedProduct.id)) ||
            (selectedProduct?.iProductId && Number(item.iProductId) === Number(selectedProduct.iProductId)) ||
            (selectedProduct?.productId && Number(item.iProductId) === Number(selectedProduct.productId)) ||
            (item.product_title &&
              selectedProduct?.title &&
              item.product_title.trim().toLowerCase() === selectedProduct.title.trim().toLowerCase());

          if (!productMatch) return false;

          // Model matching: compare numeric IDs or names
          const modelMatch =
            (selectedModel?.id && Number(item.iModelId) === Number(selectedModel.id)) ||
            (selectedModel?.iModelId && Number(item.iModelId) === Number(selectedModel.iModelId)) ||
            (selectedModel?.modelId && Number(item.iModelId) === Number(selectedModel.modelId)) ||
            (item.model_name &&
              selectedModel?.name &&
              item.model_name.trim().toLowerCase() === selectedModel.name.trim().toLowerCase());

          if (!modelMatch) return false;

          // Background matching
          if (bgId) {
            const bgMatch =
              (selectedBackgroundImage?.id && Number(item.iBackgroundId) === Number(selectedBackgroundImage.id)) ||
              (selectedBackgroundImage?.iBackgroundId && Number(item.iBackgroundId) === Number(selectedBackgroundImage.iBackgroundId)) ||
              (selectedBackgroundImage?.backgroundId && Number(item.iBackgroundId) === Number(selectedBackgroundImage.backgroundId)) ||
              (item.background_name &&
                selectedBackgroundImage?.name &&
                item.background_name.trim().toLowerCase() === selectedBackgroundImage.name.trim().toLowerCase());
            if (!bgMatch) return false;
          } else {
            // No background in current selection
            const hasNoBg =
              !item.iBackgroundId ||
              item.iBackgroundId === null ||
              item.background_name === null ||
              !item.background_name;
            if (!hasNoBg) return false;
          }

          return true;
        });

        // Sort matching items newest first
        matchedItems.sort((a, b) => {
          const timeA = new Date(a.created_at || a.createdAt || 0).getTime();
          const timeB = new Date(b.created_at || b.createdAt || 0).getTime();
          if (timeB !== timeA) return timeB - timeA;
          return (b.iQueueId || b.id || 0) - (a.iQueueId || a.id || 0);
        });

        const imagesList = [];
        const seenUrls = new Set();

        matchedItems.forEach((item) => {
          const mainUrl = item.preview_image_url || item.generated_image_url;
          if (mainUrl && !seenUrls.has(mainUrl)) {
            seenUrls.add(mainUrl);
            imagesList.push({
              url: mainUrl,
              fullUrl: item.generated_image_url || mainUrl,
              queueId: item.iQueueId || item.id,
              status: item.status || item.sStatus,
              createdAt: item.created_at,
              productTitle: item.product_title,
              modelName: item.model_name,
              backgroundName: item.background_name,
            });
          }
        });

        setExistingQueueImages(imagesList);
      } catch (err) {
        if (!isCancelled) {
          console.error("Error fetching matching queue items:", err);
          setExistingQueueImages([]);
        }
      } finally {
        if (!isCancelled) {
          setIsCheckingQueue(false);
        }
      }
    };

    fetchMatchingQueueItems();

    return () => {
      isCancelled = true;
    };
  }, [token, productId, modelId, bgId, selectionMode]);

  useEffect(() => {
    const updateNav = (swiper, nextRef, prevRef) => {
      if (swiper && nextRef?.current && prevRef?.current) {
        swiper.params.navigation.nextEl = nextRef.current;
        swiper.params.navigation.prevEl = prevRef.current;
        swiper.navigation.destroy();
        swiper.navigation.init();
        swiper.navigation.update();
      }
    };

    updateNav(productSwiperRef.current, productNextRef, productPrevRef);
    updateNav(modelSwiperRef.current, modelNextRef, modelPrevRef);
    updateNav(generatedSwiperRef.current, generatedNextRef, generatedPrevRef);
    updateNav(existingSwiperRef.current, existingNextRef, existingPrevRef);
  }, [showMainProduct, existingQueueImages]);

  const generateImage = async () => {
    setIsLoading(true);
    setError("");

    try {
      // Step 1: Create queue task
      console.log("Generation API Auth Token:", authToken?.token || authToken);
      const response = await apiHandler({
        url: `${endpoint.API_BASE_URL}${endpoint.QUEUE_TASKS}`,
        method: "POST",
        data: buildQueuePayload(),
        authToken,
      });

      if (response?.status >= 200 && response?.status < 300) {
        invalidateProductsCache();
        dispatch(decrementGenerationsLeft(1));
        triggerGenerationCountRefresh();

        const queuePayload = response?.data?.data ?? response?.data ?? {};
        const queueId =
          queuePayload?.iQueueId ??
          queuePayload?.id ??
          queuePayload?.iQueueTaskId ??
          queuePayload?.queueTaskId;
        const queueStatus = String(
          queuePayload?.status ?? queuePayload?.sStatus ?? "",
        ).toLowerCase();
        dispatch(incrementQueueStatus(queueStatus || "queued"));

        // Step 2: Trigger generation for queued items
        // Note: If status is "processing", backend already auto-started it
        // If status is "queued", we need to trigger generation manually
        if (queueId && queueStatus === "queued") {
          try {
            const generateResponse = await apiHandler({
              url: `${endpoint.API_BASE_URL}${endpoint.QUEUE_TASKS}/${queueId}/generate`,
              method: "POST",
              authToken,
            });
            if (generateResponse?.status >= 200 && generateResponse?.status < 300) {
              dispatch(moveQueueStatus({ from: "queued", to: "processing" }));
            }
          } catch (generateError) {
            // Log but don't fail - task was created even if generation trigger fails
            console.error("Failed to trigger generation:", generateError);
          }
        }

        setGeneratedImage({ front: "", back: "" });
        setShowMainProduct(false);
        navigate("/queue", { replace: true });
      } else {
        setError("Failed to add the task to the queue.");
      }
    } catch (err) {
      console.error("Queue creation error:", err);
      setError("Something went wrong while queueing the image.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleProceed = (e) => {
    e.preventDefault();
    if (!isValid) return;
    generateImage();
  };

  return {
    isValid,
    showMainProduct,
    isLoading,
    error,
    generatedImage,
    handleProceed,
    productNextRef,
    productPrevRef,
    modelNextRef,
    modelPrevRef,
    generatedNextRef,
    generatedPrevRef,
    productSwiperRef,
    modelSwiperRef,
    generatedSwiperRef,
    existingQueueImages,
    isCheckingQueue,
    existingNextRef,
    existingPrevRef,
    existingSwiperRef,
  };
};
